import { API } from "@/config/apiBase";
import React, { useState, useContext, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Phone, ArrowLeft, Check, Crown, Users, Sparkles, Zap
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { AuthContext } from "@/App";
import { getPlanForFeatures, isFamilyMember as checkIsFamilyMember } from "@/utils/planUtils";
import { trackUpgradeClicked, trackCancelClicked, trackCheckoutStarted } from "@/utils/analytics";


const PricingPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: contextUser, setUser } = useContext(AuthContext) || {};
  const [isLoading, setIsLoading] = useState(null);
  const [isReactivating, setIsReactivating] = useState(false);
  const [isSyncingBilling, setIsSyncingBilling] = useState(false);
  const [localUser, setLocalUser] = useState(null);

  // Use local user state or context user (whichever is available)
  const user = localUser || contextUser;

  // Fetch user data on mount to ensure we have the latest subscription info
  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`);
          if (response.data) {
            setLocalUser(response.data);
            if (setUser) {
              setUser(response.data);
            }
            console.log("[Pricing] User plan:", response.data.subscription_plan, "effective_plan:", response.data.effective_plan, "family_role:", response.data.family_role);
          }
        } catch (error) {
          console.log("[Pricing] Failed to fetch user data:", error);
        }
      }
    };
    fetchUserData();
  }, [setUser]);

  // Check if user is actually authenticated (has token AND user data)
  const token = localStorage.getItem("token");
  const isAuthenticated = !!(token && user?.user_id);

  // Derive plan state from user - use effective_plan for feature gating
  // IMPORTANT: Only consider plan state if authenticated
  const currentPlan = isAuthenticated ? getPlanForFeatures(user) : null;
  const isFree = isAuthenticated && currentPlan === "free";
  const isPlus = isAuthenticated && currentPlan === "plus";
  const isFamily = isAuthenticated && currentPlan === "family";
  const isPaid = isPlus || isFamily;
  const isFamilyMember = isAuthenticated && checkIsFamilyMember(user);

  // Auto-refresh subscription state after returning from Stripe Portal
  useEffect(() => {
    const billingReturn = searchParams.get('billing');
    
    if (billingReturn === 'return') {
      console.log("[Pricing] Detected return from Stripe Portal, starting subscription sync...");
      setIsSyncingBilling(true);
      
      const initialState = {
        plan: user?.subscription_plan,
        cancel: user?.subscription_cancel_at_period_end,
        period_end: user?.subscription_current_period_end
      };
      
      let pollCount = 0;
      const maxPolls = 15;
      
      const pollSubscription = async () => {
        try {
          const response = await axios.get(`${API}/auth/me`);
          if (response.data) {
            const newState = {
              plan: response.data.subscription_plan,
              cancel: response.data.subscription_cancel_at_period_end,
              period_end: response.data.subscription_current_period_end
            };
            
            const hasChanged = 
              newState.plan !== initialState.plan ||
              newState.cancel !== initialState.cancel ||
              newState.period_end !== initialState.period_end;
            
            setUser(response.data);
            pollCount++;
            
            console.log(`[Pricing] Poll ${pollCount}/${maxPolls} - changed: ${hasChanged}`, newState);
            
            if (hasChanged || pollCount >= maxPolls) {
              setIsSyncingBilling(false);
              if (hasChanged) {
                toast.success("Billing updated successfully");
              }
              const newParams = new URLSearchParams(searchParams);
              newParams.delete('billing');
              setSearchParams(newParams, { replace: true });
              return;
            }
            
            setTimeout(pollSubscription, 2000);
          }
        } catch (error) {
          console.error("[Pricing] Poll failed:", error);
          pollCount++;
          if (pollCount < maxPolls) {
            setTimeout(pollSubscription, 2000);
          } else {
            setIsSyncingBilling(false);
          }
        }
      };
      
      pollSubscription();
    }
  }, []);

  // Refresh on window focus
  useEffect(() => {
    const handleFocus = async () => {
      if (user) {
        console.log("[Pricing] Window focused, refreshing user data...");
        try {
          const response = await axios.get(`${API}/auth/me`);
          if (response.data) {
            setUser(response.data);
          }
        } catch (error) {
          console.error("[Pricing] Focus refresh failed:", error);
        }
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        handleFocus();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user, setUser]);

  // Auto-start checkout if plan query param is present (from marketing site redirect)
  useEffect(() => {
    const planFromUrl = searchParams.get('plan');
    if (planFromUrl && user && (planFromUrl === 'plus' || planFromUrl === 'family')) {
      console.log(`[Pricing] Auto-starting checkout for plan: ${planFromUrl}`);
      handleSubscribe(planFromUrl);
    }
  }, [searchParams, user]);

  // Handle reactivation - opens Stripe Portal directly
  const handleReactivate = async () => {
    setIsReactivating(true);
    try {
      const response = await axios.post(`${API}/billing/portal`);
      const { url } = response.data;
      if (url) {
        window.location.href = url;
      } else {
        toast.error("Could not open billing portal");
        setIsReactivating(false);
      }
    } catch (error) {
      console.error("[Pricing] Reactivate error:", error);
      toast.error("Could not open billing portal. Please try again.");
      setIsReactivating(false);
    }
  };

  const plans = [
    {
      id: "free",
      name: "Free",
      price: 0,
      period: "forever",
      description: "Perfect for getting started",
      icon: <Phone className="w-6 h-6" />,
      features: [
        "Up to 10 contacts",
        "Basic reminders",
        "Call logging",
        "Streak tracking"
      ],
      color: "#6D6F7C",
      popular: false,
      footnote: "Upgrade anytime as your circle grows."
    },
    {
      id: "plus",
      name: "OuttaTouch Plus",
      price: 4.99,
      period: "month",
      description: "For dedicated connectors",
      icon: <Sparkles className="w-6 h-6" />,
      features: [
        "Unlimited contacts",
        "Priority reminders",
        "Streak badges & achievements",
        "AI call summaries",
        "Ad-free experience"
      ],
      color: "#E07A5F",
      popular: true
    },
    {
      id: "family",
      name: "OuttaTouch Family",
      price: 11.99,
      period: "month",
      description: "Plus features for the whole family — one simple bill",
      icon: <Users className="w-6 h-6" />,
      features: [
        "Everything in Plus",
        "Up to 5 family members (individual accounts)",
        "Each member has private contacts & reminders",
        "Separate logins, one shared subscription",
        "Priority support"
      ],
      color: "#81B29A",
      popular: false,
      footnote: "Family members have their own private accounts. Contacts, calls, and reminders are not shared."
    }
  ];

  const handleSubscribe = async (planId) => {
    if (!user) {
      navigate("/register");
      return;
    }

    // Guard: Cannot "downgrade" to free - users must cancel instead
    // Free becomes active only after subscription period ends
    if (planId === "free") {
      // Silently ignore - Free plan button should be disabled for paid users
      return;
    }

    // Track upgrade click
    trackUpgradeClicked(planId, "pricing");
    
    setIsLoading(planId);

    try {
      // Track checkout started
      trackCheckoutStarted(planId);
      
      const response = await axios.post(`${API}/checkout/subscription`, {
        plan_id: planId,
        origin_url: window.location.origin
      });

      // Always redirect to the URL returned (checkout or portal)
      const { redirect_url, mode } = response.data;
      
      if (mode === "portal") {
        toast.info("Opening billing portal to manage your subscription...");
        // Track as cancel click if going to portal for downgrade
        if (currentPlan !== "free" && planId !== currentPlan) {
          trackCancelClicked("pricing_portal");
        }
      }
      
      window.location.href = redirect_url;
    } catch (error) {
      const message = error.response?.data?.detail?.message || error.response?.data?.detail || "Failed to start checkout";
      toast.error(typeof message === 'string' ? message : "Failed to start checkout");
      setIsLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="bg-white border-b border-[#F4F1EA] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to={user ? "/dashboard" : "/"} data-testid="back-link">
                <Button variant="ghost" size="icon" className="rounded-full">
                  <ArrowLeft className="w-5 h-5 text-[#3D405B]" />
                </Button>
              </Link>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[#E07A5F] rounded-full flex items-center justify-center">
                  <Phone className="w-4 h-4 text-white" />
                </div>
                <span className="font-bold text-lg text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
                  OuttaTouch
                </span>
              </div>
            </div>
            {user && (
              <Badge className={`${user.subscription_cancel_at_period_end ? 'bg-amber-100 text-amber-800' : isFamilyMember ? 'bg-purple-100 text-purple-800' : 'bg-[#F4F1EA] text-[#6D6F7C]'}`}>
                {isFree ? (
                  "Free"
                ) : isFamilyMember && isFamily ? (
                  "Family Plan (via family)"
                ) : user.subscription_cancel_at_period_end && user.subscription_current_period_end ? (
                  <>
                    {currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)} • Cancels{' '}
                    {new Date(user.subscription_current_period_end * 1000).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    })}
                  </>
                ) : (
                  `Current: ${currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)}`
                )}
              </Badge>
            )}
          </div>
        </div>
      </header>

      {/* Cancellation Banner - shown when subscription is set to cancel */}
      {user?.subscription_cancel_at_period_end && user?.subscription_current_period_end && (
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3">
            <div className="flex items-center justify-center gap-2 text-amber-800">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span className="font-medium">
                Your {currentPlan} plan cancels on{' '}
                <span className="font-bold">
                  {new Date(user.subscription_current_period_end * 1000).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </span>
                . You'll keep access until then.
              </span>
              <button 
                onClick={handleReactivate}
                disabled={isReactivating}
                className="ml-2 underline hover:no-underline disabled:opacity-50 disabled:cursor-wait"
                data-testid="reactivate-button"
              >
                {isReactivating ? "Opening..." : "Reactivate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 
            className="text-4xl md:text-5xl font-bold text-[#3D405B] mb-4"
            style={{ fontFamily: 'Fraunces, serif' }}
          >
            Stay Connected, Your Way
          </h1>
          <p className="text-lg text-[#6D6F7C] max-w-2xl mx-auto">
            Choose the plan that fits how you stay in touch. All plans include core calling and streak features.
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.id}
              className={`relative bg-white rounded-3xl p-8 border-2 transition-all ${
                plan.popular 
                  ? 'border-[#E07A5F] shadow-xl scale-105' 
                  : 'border-[#F4F1EA] hover:border-[#E07A5F]/30'
              }`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <Badge className="bg-[#E07A5F] text-white px-4 py-1">
                    <Crown className="w-3 h-3 mr-1" />
                    Most Popular
                  </Badge>
                </div>
              )}
              
              {/* Current Plan badge for paid plans */}
              {currentPlan === plan.id && plan.id !== "free" && (
                <div className="absolute -top-4 right-4">
                  <Badge className="bg-[#81B29A] text-white px-4 py-1">
                    <Check className="w-3 h-3 mr-1" />
                    Current Plan
                  </Badge>
                </div>
              )}

              <div 
                className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
                style={{ backgroundColor: `${plan.color}20`, color: plan.color }}
              >
                {plan.icon}
              </div>

              <h3 
                className="text-xl font-bold text-[#3D405B] mb-2"
                style={{ fontFamily: 'Fraunces, serif' }}
              >
                {plan.name}
              </h3>
              <p className="text-sm text-[#6D6F7C] mb-4">{plan.description}</p>

              <div className="mb-6">
                <span className="text-4xl font-bold text-[#3D405B]">
                  ${plan.price}
                </span>
                <span className="text-[#6D6F7C]">/{plan.period}</span>
              </div>

              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#81B29A] flex-shrink-0 mt-0.5" />
                    <span className="text-[#3D405B] text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              
              {/* Plan footnote */}
              {plan.footnote && (
                <p className="text-xs text-[#9CA3AF] italic mb-6 border-t border-[#F4F1EA] pt-4">
                  {plan.footnote}
                </p>
              )}

              {/* CTA Button - Logic based on user's subscription state */}
              {plan.id === "free" ? (
                // FREE PLAN CTA
                !isAuthenticated ? (
                  // Logged out - show Get Started
                  <Link to="/register" className="w-full">
                    <Button
                      className="w-full rounded-full py-6 font-semibold bg-[#F4F1EA] hover:bg-[#E07A5F] hover:text-white text-[#3D405B]"
                      data-testid={`subscribe-${plan.id}`}
                    >
                      Get Started Free
                    </Button>
                  </Link>
                ) : isFree ? (
                  <Button
                    disabled
                    className="w-full rounded-full py-6 font-semibold bg-[#F4F1EA] text-[#6D6F7C] cursor-not-allowed"
                    data-testid={`subscribe-${plan.id}`}
                  >
                    Current Plan
                  </Button>
                ) : (
                  <Button
                    disabled
                    variant="ghost"
                    className="w-full rounded-full py-6 font-semibold text-[#6D6F7C] cursor-not-allowed"
                    data-testid={`subscribe-${plan.id}`}
                  >
                    Available after cancellation
                  </Button>
                )
              ) : !isAuthenticated ? (
                // LOGGED OUT - PAID PLANS CTA
                <Link to="/register" className="w-full">
                  <Button
                    className={`w-full rounded-full py-6 font-semibold transition-all ${
                      plan.popular
                        ? 'bg-[#E07A5F] hover:bg-[#D0694E] text-white'
                        : 'bg-[#F4F1EA] hover:bg-[#E07A5F] hover:text-white text-[#3D405B]'
                    }`}
                    data-testid={`subscribe-${plan.id}`}
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Get Started
                  </Button>
                </Link>
              ) : currentPlan === plan.id ? (
                // CURRENT PAID PLAN CTA
                <Button
                  onClick={() => handleSubscribe(plan.id)}
                  className={`w-full rounded-full py-6 font-semibold ${
                    plan.popular
                      ? 'bg-[#E07A5F] hover:bg-[#D0694E] text-white'
                      : 'bg-[#F4F1EA] hover:bg-[#E07A5F] hover:text-white text-[#3D405B]'
                  }`}
                  data-testid={`subscribe-${plan.id}`}
                >
                  {isLoading === plan.id ? (
                    <span className="flex items-center gap-2">
                      <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full"></div>
                      Processing...
                    </span>
                  ) : (
                    "Manage Billing"
                  )}
                </Button>
              ) : (
                // UPGRADE/SWITCH PLAN CTA
                <Button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={isLoading === plan.id}
                  className={`w-full rounded-full py-6 font-semibold transition-all ${
                    plan.popular
                      ? 'bg-[#E07A5F] hover:bg-[#D0694E] text-white'
                      : 'bg-[#F4F1EA] hover:bg-[#E07A5F] hover:text-white text-[#3D405B]'
                  }`}
                  data-testid={`subscribe-${plan.id}`}
                >
                  {isLoading === plan.id ? (
                    <span className="flex items-center gap-2">
                      <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full"></div>
                      Processing...
                    </span>
                  ) : isFree ? (
                    <>
                      <Zap className="w-4 h-4 mr-2" />
                      Upgrade Now
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4 mr-2" />
                      Switch to {plan.name}
                    </>
                  )}
                </Button>
              )}
            </motion.div>
          ))}
        </div>

        {/* Referral Section */}
        <motion.div 
          className="mt-16 text-center bg-gradient-to-r from-[#E07A5F]/10 to-[#F2CC8F]/10 rounded-3xl p-8 md:p-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h2 
            className="text-2xl md:text-3xl font-bold text-[#3D405B] mb-4"
            style={{ fontFamily: 'Fraunces, serif' }}
          >
            Invite Friends, Get Free Months! 🎉
          </h2>
          <p className="text-[#6D6F7C] mb-6 max-w-xl mx-auto">
            Share your referral code with friends. When they sign up, you both get 1 month of OuttaTouch Plus free!
          </p>
          {user?.referral_code && (
            <div className="inline-flex items-center gap-4 bg-white rounded-full px-6 py-3 shadow-md">
              <span className="text-[#3D405B] font-mono font-bold text-lg">
                {user.referral_code}
              </span>
              <Button
                size="sm"
                variant="outline"
                className="rounded-full"
                onClick={() => {
                  navigator.clipboard.writeText(user.referral_code);
                  toast.success("Referral code copied!");
                }}
              >
                Copy
              </Button>
            </div>
          )}
        </motion.div>

        {/* Free Trial Callout */}
        <motion.div 
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="inline-flex items-center gap-3 bg-[#81B29A]/10 border border-[#81B29A]/30 rounded-2xl px-6 py-4">
            <div className="w-10 h-10 bg-[#81B29A]/20 rounded-full flex items-center justify-center">
              <Check className="w-5 h-5 text-[#81B29A]" />
            </div>
            <div className="text-left">
              <p className="font-semibold text-[#3D405B]">Try risk-free</p>
              <p className="text-sm text-[#6D6F7C]">
                Plus and Family include a 14-day free trial. Cancel anytime before the trial ends and you won't be charged.
              </p>
            </div>
          </div>
        </motion.div>

        {/* FAQ */}
        <div className="mt-16">
          <h2 
            className="text-2xl font-bold text-[#3D405B] text-center mb-8"
            style={{ fontFamily: 'Fraunces, serif' }}
          >
            Frequently Asked Questions
          </h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              {
                q: "How does the Family Plan work?",
                a: "The Family Plan lets you share OuttaTouch with up to 5 people under one subscription. Each family member has their own private account, contacts, reminders, and settings."
              },
              {
                q: "Do family members need their own accounts?",
                a: "Yes. Each family member creates their own login. Nothing is shared unless you choose to share it."
              },
              {
                q: "How do I invite family members?",
                a: "After upgrading to the Family Plan, you can invite members from Settings → Family. They'll receive an email with a secure invite link."
              },
              {
                q: "What happens when someone joins my family?",
                a: "Once they accept the invite, they get full access to Family features, they don't pay anything, and you manage seats and members from Settings."
              },
              {
                q: "What if a family member leaves?",
                a: "If a member leaves the family, their account switches back to the Free plan, their data remains private, and your available seat count updates automatically."
              },
              {
                q: "Is there a free trial?",
                a: "Yes. Plus and Family plans include a 14-day free trial. You can cancel anytime during the trial and won't be charged."
              },
              {
                q: "Can I upgrade or downgrade later?",
                a: "Yes. You can change plans anytime from Settings → Billing."
              },
              {
                q: "Can I cancel anytime?",
                a: "Yes. You'll keep access until the end of your current billing period."
              },
              {
                q: "What payment methods do you accept?",
                a: "We accept all major credit cards through secure Stripe checkout."
              }
            ].map((faq, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-[#F4F1EA]">
                <h3 className="font-semibold text-[#3D405B] mb-2">{faq.q}</h3>
                <p className="text-[#6D6F7C] text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default PricingPage;
